import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import InputAdornment from "@material-ui/core/InputAdornment";

import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import StatusChip from "../StatusChip";
import EmptyState from "../EmptyState";
import useConfirm from "../../hooks/useConfirm";

const KIND_META = {
  birthday: { label: "Aniversário", tone: "info" },
  renewal: { label: "Renovação", tone: "warning" },
  anniversary: { label: "Data comemorativa", tone: "info" },
  seasonal: { label: "Sazonal", tone: "neutral" },
  custom: { label: "Outra", tone: "neutral" },
};

const emptyForm = {
  label: "",
  kind: "birthday",
  baseDate: "",
  recurrence: "yearly",
  leadDays: 60,
  notes: "",
};

const fmtDate = (d) =>
  d ? new Date(`${String(d).slice(0, 10)}T12:00:00`).toLocaleDateString("pt-BR") : "—";

const useStyles = makeStyles((theme) => ({
  row: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(2),
    padding: theme.spacing(1.5, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  main: { flex: 1, minWidth: 0 },
  when: {
    minWidth: 130,
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
  },
  days: { fontWeight: 700 },
  urgent: { color: theme.palette.error.main },
  soon: { color: theme.palette.warning.main },
  date: { color: theme.palette.text.secondary, fontSize: 12, display: "block" },
  sub: { color: theme.palette.text.secondary, display: "block" },
  head: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1),
  },
  inactive: { opacity: 0.5 },
}));

/**
 * Datas-chave do cliente.
 *
 * Componente próprio, e não markup solto no ClientDetail, porque a mesma
 * lista precisa aparecer também na ficha vista de outros lugares — e porque
 * o cálculo de "quando é a próxima" vem do backend: repetir aqui a regra de
 * recorrência abriria espaço para as duas divergirem.
 */
const ClientKeyDates = ({ clientId }) => {
  const classes = useStyles();
  const [confirm, confirmDialog] = useConfirm();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dlg, setDlg] = useState({ open: false, editing: null, form: emptyForm });

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/clients/${clientId}/key-dates`);
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    load();
  }, [load]);

  const openNew = () =>
    setDlg({ open: true, editing: null, form: { ...emptyForm } });

  const openEdit = (r) =>
    setDlg({
      open: true,
      editing: r.id,
      form: {
        label: r.label || "",
        kind: r.kind || "custom",
        baseDate: String(r.baseDate || "").slice(0, 10),
        recurrence: r.recurrence || "yearly",
        leadDays: r.leadDays ?? 60,
        notes: r.notes || "",
      },
    });

  const close = () => setDlg({ open: false, editing: null, form: emptyForm });

  const save = async () => {
    const { form, editing } = dlg;
    if (!form.label?.trim() || !form.baseDate) {
      toast.warn("Informe o nome da data e a data de referência.");
      return;
    }
    try {
      if (editing) {
        await api.put(`/key-dates/${editing}`, form);
      } else {
        await api.post("/key-dates", { ...form, clientId });
      }
      toast.success("Data salva.");
      close();
      load();
    } catch (err) {
      toastError(err);
    }
  };

  const remove = async (r) => {
    const ok = await confirm({
      title: `Excluir "${r.label}"?`,
      message: "A data deixa de aparecer na fila de trabalho.",
    });
    if (!ok) return;
    try {
      await api.delete(`/key-dates/${r.id}`);
      load();
    } catch (err) {
      toastError(err);
    }
  };

  const ehAniversario = ["birthday", "anniversary"].includes(dlg.form.kind);

  return (
    <Box p={2}>
      {confirmDialog}

      <div className={classes.head}>
        <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
          Datas importantes
        </Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={openNew}>
          Nova data
        </Button>
      </div>

      {!loading && rows.length === 0 && (
        <EmptyState
          title="Nenhuma data cadastrada"
          description="Aniversário de uma criança, vencimento de plano, data de contrato — o que for cadastrado aqui aparece em 'Datas a trabalhar' com a antecedência que você definir."
          action={
            <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={openNew}>
              Nova data
            </Button>
          }
        />
      )}

      {rows.map((r) => {
        const dias = r.daysUntil;
        const cls =
          dias === null ? "" : dias <= 7 ? classes.urgent : dias <= 30 ? classes.soon : "";
        const meta = KIND_META[r.kind] || { label: r.kind, tone: "neutral" };

        return (
          <div key={r.id} className={`${classes.row} ${r.active ? "" : classes.inactive}`}>
            <div className={classes.main}>
              <Typography variant="subtitle2">
                {r.label}{" "}
                <StatusChip dot tone={meta.tone} label={meta.label} />
                {r.turningYears ? (
                  <>
                    {" "}
                    <StatusChip
                      tone="success"
                      label={`faz ${r.turningYears} ano${r.turningYears > 1 ? "s" : ""}`}
                    />
                  </>
                ) : null}
                {!r.active && <> <StatusChip tone="neutral" label="inativa" /></>}
              </Typography>
              <Typography variant="caption" className={classes.sub}>
                {r.recurrence === "yearly" ? "Todo ano" : "Uma vez"} · avisa{" "}
                {r.leadDays} dias antes
                {r.notes ? ` · ${r.notes}` : ""}
              </Typography>
            </div>

            <div className={classes.when}>
              {r.occurrence ? (
                <>
                  <span className={`${classes.days} ${cls}`}>
                    {dias === 0 ? "hoje" : `em ${dias} dias`}
                  </span>
                  <span className={classes.date}>{fmtDate(r.occurrence)}</span>
                </>
              ) : (
                <span className={classes.date}>já passou</span>
              )}
            </div>

            <div>
              <Tooltip title="Editar">
                <IconButton size="small" onClick={() => openEdit(r)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Excluir">
                <IconButton size="small" onClick={() => remove(r)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </div>
          </div>
        );
      })}

      <Dialog open={dlg.open} onClose={close} maxWidth="sm" fullWidth>
        <DialogTitle>{dlg.editing ? "Editar data" : "Nova data"}</DialogTitle>
        <DialogContent>
          <TextField
            label="Nome da data"
            fullWidth
            margin="dense"
            value={dlg.form.label}
            onChange={(e) =>
              setDlg((d) => ({ ...d, form: { ...d.form, label: e.target.value } }))
            }
            helperText='Como você a reconhece: "Aniversário da Maria", "Vencimento do plano Gold"'
          />

          <Box display="flex" style={{ gap: 8 }}>
            <TextField
              select
              label="Tipo"
              margin="dense"
              style={{ flex: 1 }}
              value={dlg.form.kind}
              onChange={(e) =>
                setDlg((d) => ({ ...d, form: { ...d.form, kind: e.target.value } }))
              }
            >
              {Object.entries(KIND_META).map(([value, m]) => (
                <MenuItem key={value} value={value}>
                  {m.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Repetição"
              margin="dense"
              style={{ flex: 1 }}
              value={dlg.form.recurrence}
              onChange={(e) =>
                setDlg((d) => ({ ...d, form: { ...d.form, recurrence: e.target.value } }))
              }
            >
              <MenuItem value="yearly">Todo ano</MenuItem>
              <MenuItem value="once">Uma vez</MenuItem>
            </TextField>
          </Box>

          <Box display="flex" style={{ gap: 8 }}>
            <TextField
              label={ehAniversario ? "Data de nascimento" : "Data de referência"}
              type="date"
              margin="dense"
              style={{ flex: 1 }}
              InputLabelProps={{ shrink: true }}
              value={dlg.form.baseDate}
              onChange={(e) =>
                setDlg((d) => ({ ...d, form: { ...d.form, baseDate: e.target.value } }))
              }
              // A data de nascimento é o que permite mostrar "vai fazer 2
              // anos" — por isso pede o nascimento, não o próximo aniversário.
              helperText={
                ehAniversario
                  ? "É dela que sai quantos anos a pessoa completa"
                  : "Quando a data acontece"
              }
            />

            <TextField
              label="Avisar com antecedência de"
              type="number"
              margin="dense"
              style={{ flex: 1 }}
              value={dlg.form.leadDays}
              onChange={(e) =>
                setDlg((d) => ({ ...d, form: { ...d.form, leadDays: e.target.value } }))
              }
              InputProps={{
                endAdornment: <InputAdornment position="end">dias</InputAdornment>,
              }}
              helperText="Quando ela entra na fila de trabalho"
            />
          </Box>

          <TextField
            label="Observações"
            fullWidth
            multiline
            rows={2}
            margin="dense"
            value={dlg.form.notes}
            onChange={(e) =>
              setDlg((d) => ({ ...d, form: { ...d.form, notes: e.target.value } }))
            }
            helperText="Aparece junto da data na fila — use para o que a pessoa precisa lembrar na hora de falar com o cliente"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>Cancelar</Button>
          <Button variant="contained" color="primary" onClick={save}>
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

ClientKeyDates.propTypes = {
  clientId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
};

export default ClientKeyDates;
